import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Card,
  CardContent,
  Container,
  makeStyles,
  lighten,
  CircularProgress,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@material-ui/core';
import { useParams } from 'react-router-dom';
import SeedAuthenticationRepository from '../../../components/repository/SeedAuthenticationRepository';
import seedDrawerRoutes from '../../../components/route/seed-drawer-routes';
import { drawerRoutesPropTypes, dashboard3DrawersIconsPropTypes } from '../../../components/page/dashboard-3-drawers/d3d-prop-types';
import seedLogo from '../../../components/images/logo.svg';
import { seedDashboard3DrawersIcons } from '../../../components/icon/seed-icons';
import DashboardDrawerBottomNav from '../../../components/page/dashboard-drawer-bottomnav/DashboardDrawerBottomNav';
import { AppState } from '../../../components/state/MyStateProvider';
import { FirebaseState } from '../../../components/firebase/MyFirebaseProvider';
import StatusRepository from '../../repository/StatusRepository';
import { MyFormattedDateTime } from '../../../components/intl';
import UserRepository from '../../repository/UserRepository';
import MyAvatar from '../../../components/widgets/avatar/MyAvatar';
import pageUtilsPropTypes from '../../../components/page/page-prop-types';

const useStyles = makeStyles((theme) => ({
  banner: {
    backgroundColor: () => (theme.palette.type === 'light'
      ? lighten(theme.palette.primary.main, 0.8) : lighten(theme.palette.primary.main, 0.2)),
    height: `${200 - (theme.spacing(2) * 2)}px`,
    display: 'flex',
    alignItems: 'flex-end',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  titleContainer: {
    paddingLeft: theme.spacing(2),
    overflow: 'hidden',
  },
  title: {
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    overflow: 'hidden',
  },
}));

const UserProfilePage = (props) => {
  const {
    AuthenticationRepository,
    drawerRoutes,
    applicationName,
    logo,
    icons,
    pageUtils,
    popoverMenus,
    bottomNavMenus,
  } = props;
  const classes = useStyles();
  const params = useParams();
  const { username } = params;

  const { state } = React.useContext(AppState);
  const { firebaseConfig } = React.useContext(FirebaseState);
  const [statusRepo] = React.useState(new StatusRepository(firebaseConfig, state));
  const [userRepo] = React.useState(new UserRepository(firebaseConfig, state));
  const [isProfileReady, setIsProfileReady] = React.useState(false);

  function prepend(value, array) {
    const newArray = array.slice();
    newArray.unshift(value);
    return newArray;
  }

  const [profile, setProfile] = React.useState(null);
  const [data, setData] = React.useReducer((oldData, { type, value, id }) => {
    switch (type) {
      case 'add_all':
        return value;
      case 'prepend':
        return prepend(value, oldData);
      case 'append':
        return [...oldData, value];
      case 'update':
        return oldData.map((d) => (d.id === id ? value : d));
      case 'remove':
        return oldData.filter((d) => d.id !== id);
      case 'clear':
        return [];
      default:
        return oldData;
    }
  }, []);

  React.useEffect(() => {
    setIsProfileReady(false);
    pageUtils.loadSomethingSilent(
      () => userRepo.getByUsername(username),
      (payload) => {
        console.log(payload);
        setProfile(payload);
        setIsProfileReady(true);
      },
      (error) => {
        pageUtils.showSnackbar(error.message, 'error');
      },
      () => {

      },
    );
    const unsubscribe = statusRepo.getAllByUsernameLive(username)
      .onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            console.log('New city: ', change.doc.data());
            const newStatus = change.doc.data();
            setData({
              type: 'prepend',
              value: {
                id: change.doc.id,
                ...newStatus,
              },
            });
          }
          if (change.type === 'modified') {
            console.log('Modified city: ', change.doc.data());
            const newStatus = change.doc.data();
            setData({
              type: 'update',
              id: change.doc.id,
              value: {
                id: change.doc.id,
                ...newStatus,
              },
            });
          }
          if (change.type === 'removed') {
            console.log('Removed city: ', change.doc.data());
            setData({
              type: 'remove',
              id: change.doc.id,
            });
          }
        });
      });

    return () => {
      unsubscribe();
    };
  }, [statusRepo, userRepo, pageUtils, username]);

  return (
    <DashboardDrawerBottomNav
      titleId="rafflesia.home.title"
      routes={drawerRoutes}
      AuthenticationRepository={AuthenticationRepository}
      applicationName={applicationName}
      logo={logo}
      icons={icons}
      popoverMenus={popoverMenus}
      bottomNavMenus={bottomNavMenus}
      usePaddingContent={false}
    >
      <Container className={classes.banner}>
        <MyAvatar
          size={60}
          src={profile ? profile.photoURL : ''}
          alt={profile ? profile.username : ''}
          name={profile ? profile.username : ''}
        />
        <div className={classes.titleContainer}>
          <Typography className={classes.title} variant="h5" color="textPrimary">
            {profile ? profile.username : username}
          </Typography>
          <Typography className={classes.title} variant="body2" color="textSecondary">
            {profile ? profile.username : ''}
          </Typography>
        </div>
        <div className={classes.spacer} />
      </Container>
      {
        !isProfileReady ? (
          <Box align="center">
            <CircularProgress />
          </Box>
        )
          : data.map((status) => (
            <Card>
              <CardContent>
                <ListItem>
                  <ListItemAvatar>
                    <MyAvatar
                      src={profile ? profile.photoURL : ''}
                      alt={profile ? profile.username : ''}
                      name={profile ? profile.username : ''}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={profile.username}
                    secondary={(
                      <MyFormattedDateTime
                        value={new Date(status.timestamp.seconds * 1000)}
                      />
                  )}
                  />
                </ListItem>
                <Typography variant="body1">
                  {status.message}
                </Typography>
                <Typography variant="caption">
                  {status.comments}
                </Typography>
              </CardContent>
            </Card>
          ))
      }
    </DashboardDrawerBottomNav>
  );
};

UserProfilePage.propTypes = {
  AuthenticationRepository: PropTypes.func,
  applicationName: PropTypes.string,
  logo: PropTypes.string,
  drawerRoutes: drawerRoutesPropTypes,
  icons: dashboard3DrawersIconsPropTypes,
  pageUtils: pageUtilsPropTypes.isRequired,
};

UserProfilePage.defaultProps = {
  AuthenticationRepository: SeedAuthenticationRepository,
  applicationName: 'react-seed',
  logo: seedLogo,
  drawerRoutes: seedDrawerRoutes,
  icons: seedDashboard3DrawersIcons,
};

export default UserProfilePage;
